import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Heading, Panel } from '@savgroup-front-common/core';
import { Button } from '@savgroup-front-common/core/src/atoms/button';
import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Datatable } from '@savgroup-front-common/core/src/components/Datatable';
import { Drawer } from '@savgroup-front-common/core/src/atoms/Drawer';
import { DrawerPosition } from '@savgroup-front-common/core/src/atoms/Drawer/Drawer.types';
import { Input } from '@savgroup-front-common/core/src/atoms/Form';
import { Trash, Bookmark, Pin, ClockRotateRight } from 'iconoir-react';
import { ChevronDownIcon, ChevronRightIcon } from '@savgroup-front-common/core/src/protons/icons';
import styled from 'styled-components';

interface ContexteDetails {
  statutCible?: string;
  statutFin?: string;
  solution?: string;
  typeClient?: string;
  marqueProduit?: string;
  categorieProduit?: string;
  acteur?: string;
  garantie?: string;
}

interface Notification {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'draft';
  contexte: string;
  contexteDetails?: ContexteDetails;
  cible: string;
  template: string;
  relance?: number;
  templateRelance?: string;
  createdAt: string;
  updatedAt: string;
}

interface RelanceItem {
  id: string;
  jours: string;
  useDifferentTemplate: boolean;
  template: string;
  statutFin: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const STATUT_DECLENCHEUR_OPTIONS: SelectOption[] = [
  { value: 'en_attente_depot', label: 'En attente de dépôt' },
  { value: 'depot_effectue', label: 'Dépôt effectué' },
  { value: 'devis_a_valider', label: 'Devis à valider' },
  { value: 'produit_a_recuperer', label: 'Produit à récupérer' },
  { value: 'produit_receptionné', label: 'Produit réceptionné par le prestataire' },
];

const SOLUTION_OPTIONS: SelectOption[] = [
  { value: 'prise_en_charge_centre', label: 'Prise en charge en centre de service' },
  { value: 'remboursement', label: 'Remboursement' },
  { value: 'reparation', label: 'Réparation' },
  { value: 'intervention_domicile', label: 'Intervention à domicile' },
];

const MARQUE_OPTIONS: SelectOption[] = [
  { value: 'apple', label: 'Apple' },
  { value: 'acer', label: 'Acer' },
  { value: 'samsung', label: 'Samsung' },
  { value: 'lg', label: 'LG' },
];

const CATEGORIE_OPTIONS: SelectOption[] = [
  { value: 'machine_a_laver', label: 'Machine à laver' },
  { value: 'ordinateur', label: 'Ordinateur' },
  { value: 'aspirateur', label: 'Aspirateur' },
];

const GARANTIE_OPTIONS: SelectOption[] = [
  { value: 'sous_garantie_distributeur', label: 'Sous garantie distributeur' },
  { value: 'retractation', label: 'Rétractation' },
  { value: 'panne_au_deballage', label: 'Panne au déballage' },
  { value: 'hors_garantie', label: 'Hors garantie' },
];

const ACTEUR_OPTIONS: SelectOption[] = [
  { value: 'apple_europe', label: 'Apple Europe' },
  { value: 'nintendo_europe', label: 'Nintendo Europe' },
  { value: 'samsung', label: 'Samsung' },
  { value: 'sds', label: 'SDS' },
  { value: 'nls', label: 'NLS' },
];

const DESTINATAIRE_OPTIONS: SelectOption[] = [
  { value: 'client', label: 'Client' },
  { value: 'acteur', label: 'Acteur' },
  { value: 'repairer', label: 'Reparateur' },
  { value: 'supplier', label: 'Fournisseur' },
  { value: 'magasin_referent', label: 'Magasin référent' },
];

const findSelectValueByLabel = (options: SelectOption[], label: string | undefined): string => {
  if (!label) {
    return '';
  }

  const found = options.find((o) => o.label === label);

  return found?.value ?? '';
};

const findSelectLabelFromValue = (options: SelectOption[], value: string): string => {
  if (!value) {
    return '';
  }

  return options.find((o) => o.value === value)?.label ?? value;
};

const templateStorageToFormInput = (template: string): string => {
  if (!template) {
    return '';
  }

  const withoutCulture = template.replace(/_[A-Z]{2}$/, '');

  return withoutCulture.replace(/_/g, ' ');
};

const resolveDestinataireFormValue = (cibleDisplay: string): string => {
  const byLabel = DESTINATAIRE_OPTIONS.find((o) => o.label === cibleDisplay);
  if (byLabel) {
    return byLabel.value;
  }
  if (cibleDisplay === 'Magasin') {
    return 'magasin_referent';
  }

  return '';
};

const typeClientLabelToFormValue = (label: string | undefined): string => {
  if (label === 'Pro') {
    return 'pro';
  }
  if (label === 'Individuel') {
    return 'individuel';
  }

  return '';
};

const buildFormDataFromNotification = (notification: Notification) => {
  const d = notification.contexteDetails;
  const relances: RelanceItem[] = [];
  if (
    notification.relance !== undefined ||
    notification.templateRelance ||
    (d?.statutFin && d.statutFin.trim() !== '')
  ) {
    const tpl = notification.templateRelance ? templateStorageToFormInput(notification.templateRelance) : '';
    relances.push({
      id: `relance-edit-${notification.id}`,
      jours: notification.relance !== undefined ? String(notification.relance) : '',
      useDifferentTemplate: Boolean(tpl),
      template: tpl,
      statutFin: d?.statutFin?.trim() ?? '',
    });
  }

  return {
    nomCampagne: notification.name,
    statutCible: findSelectValueByLabel(STATUT_DECLENCHEUR_OPTIONS, d?.statutCible),
    solution: findSelectValueByLabel(SOLUTION_OPTIONS, d?.solution),
    typeClient: typeClientLabelToFormValue(d?.typeClient),
    marqueProduit: findSelectValueByLabel(MARQUE_OPTIONS, d?.marqueProduit),
    categorieProduit: findSelectValueByLabel(CATEGORIE_OPTIONS, d?.categorieProduit),
    acteur: findSelectValueByLabel(ACTEUR_OPTIONS, d?.acteur),
    garantie: findSelectValueByLabel(GARANTIE_OPTIONS, d?.garantie),
    templateEmail: templateStorageToFormInput(notification.template),
    cible: resolveDestinataireFormValue(notification.cible),
    type: notification.type,
    relances,
  };
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DrawerContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: 100%;
`;

const DrawerFooter = styled.div`
  order: 4;
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
`;

const DrawerHeader = styled.div`
  order: 0;
  flex-shrink: 0;
  margin-bottom: 1rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.newUI.variationColors?.light3 || '#e0e0e0'};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.newUI.defaultColors.text || '#333'};
`;

const CollapsibleSectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HelperText = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f5f5f5'};
  border-left: 3px solid ${({ theme }) => theme.colors?.primary || '#2196f3'};
  border-radius: 4px;
  color: ${({ theme }) => theme.newUI.variationColors?.dark1 || '#555'};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const TypeClientLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: ${({ theme }) => theme.fonts?.size?.megaSmall ?? theme.fonts?.size?.small ?? '0.875rem'};
  font-weight: ${({ theme }) => theme.fonts?.weight?.mediumFont ?? '500'};
  color: ${({ theme }) =>
    theme.newUI?.defaultColors?.secondaryText ?? theme.colors?.paragraphTextColor ?? '#5c616b'};
`;

const RequiredAsterisk = styled.span`
  color: #f44336;
  margin-left: 0.2rem;
`;

const TypeClientSelectTrigger = styled(SelectPrimitive.Trigger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 0 0.75rem;
  box-sizing: border-box;
  font-size: ${({ theme }) => theme.fonts?.size?.ultraSmall ?? theme.fonts?.size?.small ?? '0.875rem'};
  font-weight: ${({ theme }) => theme.fonts?.weight?.normal ?? '400'};
  color: ${({ theme }) =>
    theme.newUI?.defaultColors?.text ?? theme.colors?.mainTextColor ?? '#343434'};
  background-color: ${({ theme }) =>
    theme.systemColors?.defaultBackground ?? theme.colors?.white ?? '#fff'};
  border: 1px solid ${({ theme }) =>
    theme.newUI?.variationColors?.light4 ?? theme.colors?.default ?? '#e0e0e0'};
  border-radius: ${({ theme }) =>
    theme.newUI?.borders?.mediumRadius ?? theme.borders?.radius ?? '4px'};
  cursor: pointer;
  outline: none;
  gap: 0.5rem;

  &[data-placeholder],
  & [data-placeholder] {
    color: ${({ theme }) =>
      theme.newUI?.defaultColors?.secondaryText ?? theme.colors?.paragraphTextColor ?? '#5c616b'} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors?.primary ?? '#5369eb'};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors?.primary ?? '#5369eb'};
  }
`;

const TypeClientSelectContent = styled(SelectPrimitive.Content)`
  overflow: hidden;
  background-color: ${({ theme }) =>
    theme.systemColors?.defaultBackground ?? theme.colors?.white ?? '#fff'};
  border: 1px solid ${({ theme }) =>
    theme.newUI?.variationColors?.light4 ?? theme.colors?.default ?? '#e0e0e0'};
  border-radius: ${({ theme }) =>
    theme.newUI?.borders?.mediumRadius ?? theme.borders?.radius ?? '4px'};
  box-shadow: ${({ theme }) => theme.newUI?.shadows?.level1 ?? '0 4px 12px rgba(0,0,0,0.1)'};
  padding: 4px;
  z-index: 9999;
`;

const TypeClientSelectItem = styled(SelectPrimitive.Item)`
  font-size: ${({ theme }) => theme.fonts?.size?.ultraSmall ?? theme.fonts?.size?.small ?? '0.875rem'};
  font-weight: ${({ theme }) => theme.fonts?.weight?.normal ?? '400'};
  color: ${({ theme }) =>
    theme.newUI?.defaultColors?.text ?? theme.colors?.mainTextColor ?? '#343434'};
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.newUI?.borders?.smallRadius ?? '2px'};
  cursor: pointer;
  outline: none;

  &[data-highlighted] {
    background-color: ${({ theme }) =>
      theme.newUI?.variationColors?.light5 ?? theme.colors?.lightGrey ?? '#f5f5f5'};
  }
`;

const TypeClientSelectIcon = styled(SelectPrimitive.Icon)`
  display: flex;
  align-items: center;
  color: ${({ theme }) =>
    theme.newUI?.defaultColors?.secondaryText ?? theme.colors?.paragraphTextColor ?? '#5c616b'};
`;

const RelanceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RelanceItem = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f9f9f9'};
  border: 1px solid ${({ theme }) => theme.newUI.variationColors?.light3 || '#e0e0e0'};
  border-radius: ${({ theme }) => theme.newUI.borders?.smallRadius || '4px'};
`;

const RelanceFields = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RelanceTopRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const JoursFieldContainer = styled.div`
  flex: 0 0 170px;
`;

const ToggleFieldContainer = styled.div`
  flex: 1;
  margin-top: 31px;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background-color: transparent;
  color: #f44336;
  border: none;
  border-radius: ${({ theme }) => theme.newUI.borders?.smallRadius || '4px'};
  cursor: pointer;
  min-width: 32px;
  min-height: 32px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(244, 67, 54, 0.1);
    color: #d32f2f;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.newUI.borders?.smallRadius || '4px'};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  align-self: flex-start;
  
  &:hover {
    opacity: 0.9;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchAndFiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterIconWrapper = styled.div`
  margin-right: 0.3rem;
  display: flex;
  align-items: center;
`;

const FilterNumber = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 4px 1px;
  margin: 0 0 0 8px;
  background: #5369eb;
  border-radius: 4px;
  font-weight: ${({ theme }) => theme.fonts?.weight?.bold || 'bold'};
  font-size: ${({ theme }) => theme.fonts?.size?.ultraMini || '10px'};
  line-height: 9px;
  text-transform: uppercase;
  color: #fff;
`;

const FilterButtonStyled = styled.button<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 7px 8px;
  background: ${({ theme }) => theme.systemColors?.defaultBackground || '#fff'};
  border: 1px solid ${({ $isActive }) => ($isActive ? '#a2abe9' : '#ccd2f9')};
  border-radius: 4px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fonts?.weight?.mediumFont || '500'};
  font-size: ${({ theme }) => theme.fonts?.size?.ultraSmall || '12px'};
  line-height: 1;
  text-align: center;
  color: ${({ theme }) => theme.colors?.mainTextColor || '#333'};
  cursor: pointer;
  height: 32px;
  position: relative;
  box-sizing: border-box;
  margin: 0;

  &:hover {
    background-color: ${({ theme }) => theme.newUI?.variationColors?.light4 || '#f5f5f5'};
    border: 1px solid #5a72ff;
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    background-color: ${({ theme }: any) => theme.systemColors?.defaultBackground || '#fff'};
    border-color: #ccd2f9;
  `}
`;

const FilterChevronWrapper = styled.div<{ $isMenuOpen?: boolean }>`
  display: flex;
  margin-left: 0.25rem;
  transition: transform 150ms cubic-bezier(0.47, 1.64, 0.41, 0.8);
  transform: rotate(${({ $isMenuOpen }) => ($isMenuOpen ? '180deg' : '0deg')});
`;

const FilterMenuDropdown = styled.div<{ $isOpen: boolean; $top: number; $left: number }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  background: white;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
`;

const FilterMenuItem = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.newUI?.variationColors?.light5 || '#f5f5f5'};
  }

  input[type='checkbox'] {
    margin-right: 0.5rem;
    cursor: pointer;
  }
`;

const SearchInputWrapper = styled.div`
  flex: 0 0 auto;
  min-width: 200px;
  flex-grow: 1;
  max-width: 300px;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  font-size: 0.875rem;
  height: 32px;
  box-sizing: border-box;
  line-height: 1;
  margin: 0;

  &:focus {
    outline: none;
    border-color: #5a72ff;
  }
`;

const RowActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const RowActionButton = styled.button`
  border: 1px solid #ccd2f9;
  background: #fff;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #5c616b;
  font-size: 16px;
  line-height: 1;

  &:hover {
    border-color: #5a72ff;
    color: #5a72ff;
  }
`;

const RowActionMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  display: flex;
  flex-direction: column;
  min-width: 150px;
  background: #fff;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  padding: 0.25rem;
`;

const RowActionMenuItem = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.5rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #343434;

  &:hover {
    background: #f5f7ff;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ConfirmModal = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 1.25rem;
`;

const ConfirmTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #333;
`;

const ConfirmText = styled.p`
  margin: 0 0 1.25rem 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #5c616b;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const ConfirmButton = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ $primary }) => ($primary ? '#5369eb' : '#ccd2f9')};
  background: ${({ $primary }) => ($primary ? '#5369eb' : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#343434')};
  border-radius: 4px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors?.disabled || '#e0e0e0'};
  border-radius: ${({ theme }) => theme.newUI.borders?.smallRadius || '4px'};
  cursor: pointer;
  min-width: 24px;
  min-height: 24px;
  transition: all 0.2s ease;
  color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f5f5f5'};
    border-color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContexteContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContexteSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #555;
`;

const ContexteDetails = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.25rem;
  padding-left: 1.5rem;
  padding-top: 0.5rem;
  font-size: 0.8125rem;
  color: #666;
  
  > div {
    display: flex;
    gap: 0.5rem;
    
    > span:first-child {
      font-weight: 500;
      color: #555;
      min-width: 120px;
    }
  }
`;

const ContexteExpand: React.FC<{ notification: Notification }> = ({ notification }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { contexte, contexteDetails } = notification;

  const hasDetails = contexteDetails && Object.keys(contexteDetails).length > 0;

  return (
    <ContexteContainer>
      <ContexteSummary>
        {hasDetails && (
          <ExpandButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            aria-label={isExpanded ? 'Replier' : 'Déplier'}
          >
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </ExpandButton>
        )}
        <span>{contexte}</span>
      </ContexteSummary>
      {hasDetails && (
        <ContexteDetails $isExpanded={isExpanded}>
          {contexteDetails.statutCible && (
            <div>
              <span>Statut déclencheur:</span>
              <span>{contexteDetails.statutCible}</span>
            </div>
          )}
          {contexteDetails.solution && (
            <div>
              <span>Solution:</span>
              <span>{contexteDetails.solution}</span>
            </div>
          )}
          {contexteDetails.typeClient && (
            <div>
              <span>Type client:</span>
              <span>{contexteDetails.typeClient}</span>
            </div>
          )}
          {contexteDetails.statutFin && (
            <div>
              <span>Statut fin:</span>
              <span>{contexteDetails.statutFin}</span>
            </div>
          )}
          {contexteDetails.marqueProduit && (
            <div>
              <span>Marque produit:</span>
              <span>{contexteDetails.marqueProduit}</span>
            </div>
          )}
          {contexteDetails.categorieProduit && (
            <div>
              <span>Catégorie produit:</span>
              <span>{contexteDetails.categorieProduit}</span>
            </div>
          )}
          {contexteDetails.acteur && (
            <div>
              <span>Acteur:</span>
              <span>{contexteDetails.acteur}</span>
            </div>
          )}
          {contexteDetails.garantie && (
            <div>
              <span>Garantie:</span>
              <span>{contexteDetails.garantie}</span>
            </div>
          )}
        </ContexteDetails>
      )}
    </ContexteContainer>
  );
};

interface FilterState {
  cible: string[];
  status: string[];
  relance: string[];
  publication: string[];
  statutDeclencheur: string[];
}

const TemplateToggleContainer = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.newUI?.defaultColors?.text ?? '#343434'};
  min-height: 32px;
`;

const TemplateToggleInput = styled.input`
  width: 40px;
  height: 22px;
  accent-color: ${({ theme }) => theme.colors?.primary ?? '#5369eb'};
  cursor: pointer;
`;

const NotificationPage = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingNotificationId, setEditingNotificationId] = useState<string | null>(null);
  const [drawerContentReady, setDrawerContentReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFilterMenu, setOpenFilterMenu] = useState<string | null>(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isAdditionalSectionOpen, setIsAdditionalSectionOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    cible: [],
    status: [],
    relance: [],
    publication: [],
    statutDeclencheur: [],
  });
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const rowActionMenuRef = useRef<HTMLDivElement>(null);
  const [openRowActionId, setOpenRowActionId] = useState<string | null>(null);
  const [rowActionMenuPosition, setRowActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [statusActionTarget, setStatusActionTarget] = useState<Notification | null>(null);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive' | null>(null);

  // Fermer le menu de filtre quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setOpenFilterMenu(null);
      }
    };

    if (openFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openFilterMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rowActionMenuRef.current && !rowActionMenuRef.current.contains(event.target as Node)) {
        setOpenRowActionId(null);
      }
    };

    if (openRowActionId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openRowActionId]);

  // Afficher le contenu du drawer après la fin de l'animation d'ouverture pour éviter le tremblement
  useEffect(() => {
    if (!isDrawerOpen) {
      setDrawerContentReady(false);
      return;
    }
    const timer = setTimeout(() => setDrawerContentReady(true), 320);
    return () => clearTimeout(timer);
  }, [isDrawerOpen]);
  
  const [allNotifications, setAllNotifications] = useState<Notification[]>([
    {
      id: '1',
      name: 'Réception confirmée',
      type: 'Email',
      status: 'active',
      contexte: 'Statut déclencheur : Dépôt effectué',
      contexteDetails: {
        statutCible: 'Dépôt effectué',
        marqueProduit: 'Samsung',
        categorieProduit: 'Électroménager',
      },
      cible: 'Client',
      template: 'reception_FR',
      relance: 3,
      templateRelance: 'relance_reception_FR',
      createdAt: '2024-01-15',
      updatedAt: '2024-01-20',
    },
    {
      id: '2',
      name: 'Relance fournisseur',
      type: 'Email',
      status: 'active',
      contexte: 'Statut déclencheur : Produit réceptionné par le prestataire',
      contexteDetails: {
        statutCible: 'Produit réceptionné par le prestataire',
        acteur: 'Fournisseur',
        garantie: 'Oui',
      },
      cible: 'Acteur',
      template: 'relance_EN',
      relance: 5,
      templateRelance: 'relance_fournisseur_EN',
      createdAt: '2024-01-10',
      updatedAt: '2024-01-18',
    },
    {
      id: '3',
      name: 'Devis à valider',
      type: 'Email',
      status: 'draft',
      contexte: 'Statut déclencheur : En attente de validation du devis',
      contexteDetails: {
        statutCible: 'En attente de validation du devis',
        statutFin: 'Validé',
      },
      cible: 'Client',
      template: 'devis_FR',
      createdAt: '2024-01-05',
      updatedAt: '2024-01-12',
    },
    {
      id: '4',
      name: 'Votre produit est envoyé',
      type: 'Email',
      status: 'draft',
      contexte: 'Statut: Résolu',
      contexteDetails: {
        statutCible: 'Résolu',
        marqueProduit: 'LG',
        categorieProduit: 'Télévision',
      },
      cible: 'Magasin',
      template: 'envoi_ES',
      relance: 7,
      templateRelance: 'relance_envoi_ES',
      createdAt: '2024-01-08',
      updatedAt: '2024-01-15',
    },
  ]);

  const getInitialFormData = () => ({
    nomCampagne: '',
    statutCible: '',
    solution: '',
    typeClient: '',
    marqueProduit: '',
    categorieProduit: '',
    acteur: '',
    garantie: '',
    templateEmail: '',
    cible: '',
    type: 'Email',
    relances: [] as RelanceItem[],
  });

  const [formData, setFormData] = useState(getInitialFormData);

  // Générer les options de filtres à partir des données
  const filterOptions = useMemo(() => {
    const cibles = Array.from(new Set(allNotifications.map(n => n.cible)));
    const statuts = Array.from(new Set(allNotifications.map(n => n.status)));
    const relances = Array.from(new Set(allNotifications.map(n => n.relance).filter(Boolean)));
    const statutsDeclencheurs = Array.from(
      new Set(allNotifications.map(n => n.contexteDetails?.statutCible).filter(Boolean))
    );

    return {
      cible: cibles.map(cible => ({
        value: cible,
        label: cible,
        count: allNotifications.filter(n => n.cible === cible).length,
      })),
      status: statuts.map(status => ({
        value: status,
        label: status === 'active' ? 'Publiée' : status === 'draft' ? 'Brouillon' : 'Inactif',
        count: allNotifications.filter(n => n.status === status).length,
      })),
      relance: relances.map(relance => ({
        value: relance,
        label: `${relance} jours`,
        count: allNotifications.filter(n => n.relance === relance).length,
      })),
      statutDeclencheur: statutsDeclencheurs.map((statutDeclencheur) => ({
        value: statutDeclencheur,
        label: statutDeclencheur,
        count: allNotifications.filter((n) => n.contexteDetails?.statutCible === statutDeclencheur).length,
      })),
      publication: [
        {
          value: 'active',
          label: 'Publiée',
          count: allNotifications.filter(n => n.status === 'active').length,
        },
        {
          value: 'draft',
          label: 'Brouillon',
          count: allNotifications.filter(n => n.status === 'draft').length,
        },
      ],
    };
  }, [allNotifications]);

  // Filtrer les notifications en fonction de la recherche et des filtres
  const filteredNotifications = useMemo(() => {
    let filtered = [...allNotifications];

    // Filtre par recherche textuelle sur le nom
    if (searchQuery.trim()) {
      filtered = filtered.filter(notification =>
        notification.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filtre par destinataire
    if (selectedFilters.cible.length > 0) {
      filtered = filtered.filter(n => selectedFilters.cible.includes(n.cible));
    }

    // Filtre par statut
    if (selectedFilters.status.length > 0) {
      filtered = filtered.filter(n => selectedFilters.status.includes(n.status));
    }

    if (selectedFilters.publication.length > 0) {
      filtered = filtered.filter(n => selectedFilters.publication.includes(n.status));
    }

    if (selectedFilters.statutDeclencheur.length > 0) {
      filtered = filtered.filter((n) => {
        const statutDeclencheur = n.contexteDetails?.statutCible;
        if (!statutDeclencheur) return false;
        return selectedFilters.statutDeclencheur.includes(statutDeclencheur);
      });
    }

    // Filtre par relance
    if (selectedFilters.relance.length > 0) {
      filtered = filtered.filter(n => {
        if (!n.relance) return false;
        return selectedFilters.relance.includes(String(n.relance));
      });
    }

    return filtered;
  }, [allNotifications, searchQuery, selectedFilters]);

  const handleFilterToggle = (
    filterType: 'cible' | 'status' | 'relance' | 'publication' | 'statutDeclencheur',
    value: string | number
  ) => {
    setSelectedFilters((prev) => {
      const currentValues = prev[filterType];
      const valueStr = String(value);
      const isSelected = currentValues.includes(valueStr);

      return {
        ...prev,
        [filterType]: isSelected
          ? currentValues.filter((v) => v !== valueStr)
          : [...currentValues, valueStr],
      };
    });
  };

  const handleFilterButtonClick = (filterType: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const isOpen = openFilterMenu === filterType;

    if (isOpen) {
      setOpenFilterMenu(null);
    } else {
      // getBoundingClientRect() donne les coordonnées par rapport à la fenêtre visible
      // Compatible avec position: fixed
      setFilterMenuPosition({
        top: buttonRect.bottom + 4,
        left: buttonRect.left,
      });
      setOpenFilterMenu(filterType);
    }
  };

  const getFilterIcon = (filterType: string) => {
    const isActive = selectedFilters[filterType as keyof FilterState]?.length > 0;
    const color = isActive || openFilterMenu === filterType ? '#5a72ff' : '#5c616b';

    switch (filterType) {
      case 'cible':
        return <Bookmark width="18px" height="18px" color={color} />;
      case 'status':
        return <Pin width="18px" height="18px" color={color} />;
      case 'relance':
        return <ClockRotateRight width="18px" height="18px" color={color} />;
      case 'publication':
        return <Pin width="18px" height="18px" color={color} />;
      case 'statutDeclencheur':
        return <Pin width="18px" height="18px" color={color} />;
      default:
        return null;
    }
  };

  const getFilterLabel = (filterType: string) => {
    switch (filterType) {
      case 'cible':
        return 'Destinataire';
      case 'status':
        return 'Statut';
      case 'relance':
        return 'Relance';
      case 'publication':
        return 'Publication';
      case 'statutDeclencheur':
        return 'Statut déclencheur';
      default:
        return '';
    }
  };

  const openNotificationForEdit = useCallback((notification: Notification) => {
    setFormData(buildFormDataFromNotification(notification));
    setEditingNotificationId(notification.id);
    const d = notification.contexteDetails;
    const hasRappel =
      notification.relance !== undefined ||
      Boolean(notification.templateRelance) ||
      Boolean(d?.statutFin?.trim());
    setIsAdditionalSectionOpen(hasRappel);
    setOpenRowActionId(null);
    setIsDrawerOpen(true);
  }, []);

  // Fonction pour formater les templates : enlever les codes de culture et mettre la première lettre en majuscule
  const formatTemplate = (template: string): string => {
    if (!template) return '';
    // Enlever les codes de culture (_FR, _EN, _ES, etc.)
    const withoutCulture = template.replace(/_[A-Z]{2}$/, '');
    // Mettre la première lettre en majuscule
    return withoutCulture.charAt(0).toUpperCase() + withoutCulture.slice(1);
  };

  const columnHelper = createColumnHelper<Notification>();

  const columns = useMemo(
    () => [
      // Colonne principale : Nom de la campagne
      columnHelper.accessor('name', {
        header: 'Campagne',
        cell: (info) => (
          <div style={{ fontWeight: 600, color: '#333' }}>
            {info.getValue()}
          </div>
        ),
        size: 200,
      }),
      // Cible : Qui reçoit la notification
      columnHelper.accessor('cible', {
        header: 'Destinataire',
        cell: (info) => {
          const cible = info.getValue();
          const cibleColors: Record<string, { bg: string; color: string }> = {
            'Client': { bg: '#e3f2fd', color: '#1976d2' },
            'Acteur': { bg: '#f3e5f5', color: '#7b1fa2' },
            'Magasin': { bg: '#e8f5e9', color: '#2e7d32' },
          };
          const colors = cibleColors[cible] || { bg: '#f5f5f5', color: '#666' };
          return (
            <span
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                backgroundColor: colors.bg,
                color: colors.color,
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {cible}
            </span>
          );
        },
        size: 130,
      }),
      // Segment : Quand se déclenche la notification
      columnHelper.accessor('contexte', {
        header: 'Segment',
        cell: (info) => <ContexteExpand notification={info.row.original} />,
        size: 420,
      }),
      // Template principal
      columnHelper.accessor('template', {
        header: 'Template',
        cell: (info) => (
          <div style={{ fontSize: '0.875rem' }}>
            {formatTemplate(info.getValue())}
          </div>
        ),
        size: 180,
      }),
      // Paramètres de relance
      columnHelper.accessor('relance', {
        header: 'Relance',
        cell: (info) => {
          const value = info.getValue();
          return value ? (
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {value} jours
            </span>
          ) : (
            <span style={{ color: '#999', fontSize: '0.875rem' }}>-</span>
          );
        },
        size: 100,
      }),
      columnHelper.accessor('templateRelance', {
        header: 'Template du rappel',
        cell: (info) => {
          const value = info.getValue();
          return value ? (
            <div style={{ fontSize: '0.875rem' }}>{formatTemplate(value)}</div>
          ) : (
            <span style={{ color: '#999', fontSize: '0.875rem' }}>-</span>
          );
        },
        size: 180,
      }),
      // Statut : Actif/Inactif
      columnHelper.accessor('status', {
        header: 'Statut',
        cell: (info) => {
          const status = info.getValue();
          const statusColorMap: Record<Notification['status'], { backgroundColor: string; color: string; label: string }> = {
            active: { backgroundColor: '#e8f5e9', color: '#2e7d32', label: 'Publiée' },
            inactive: { backgroundColor: '#ffebee', color: '#c62828', label: 'Inactif' },
            draft: { backgroundColor: '#fff3e0', color: '#ef6c00', label: 'Brouillon' },
          };
          const statusConfig = statusColorMap[status];
          return (
            <span
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                backgroundColor: statusConfig.backgroundColor,
                color: statusConfig.color,
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {statusConfig.label}
            </span>
          );
        },
        size: 110,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const notification = info.row.original;
          const notificationId = notification.id;
          const isOpen = openRowActionId === notificationId;
          const togglePublishLabel = notification.status === 'active' ? 'Désactiver' : 'Activer';

          return (
            <RowActionContainer ref={isOpen ? rowActionMenuRef : null}>
              <RowActionButton
                type="button"
                aria-label="Actions notification"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setRowActionMenuPosition({
                    top: rect.bottom + 4,
                    left: Math.max(8, rect.right - 150),
                  });
                  setOpenRowActionId((prev) => (prev === notificationId ? null : notificationId));
                }}
              >
                ...
              </RowActionButton>
              {isOpen && (
                <RowActionMenu ref={rowActionMenuRef} $top={rowActionMenuPosition.top} $left={rowActionMenuPosition.left}>
                  <RowActionMenuItem
                    type="button"
                    onClick={() => {
                      openNotificationForEdit(notification);
                    }}
                  >
                    Modifier la notification
                  </RowActionMenuItem>
                  <RowActionMenuItem
                    type="button"
                    onClick={() => {
                      setOpenRowActionId(null);
                      setStatusActionTarget(notification);
                      setPendingStatus(notification.status === 'active' ? 'inactive' : 'active');
                    }}
                  >
                    {togglePublishLabel}
                  </RowActionMenuItem>
                  <RowActionMenuItem type="button" onClick={() => setOpenRowActionId(null)}>
                    Supprimer la communication
                  </RowActionMenuItem>
                </RowActionMenu>
              )}
            </RowActionContainer>
          );
        },
        size: 60,
      }),
    ],
    [columnHelper, openRowActionId, openNotificationForEdit]
  );

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleRelanceChange = (id: string, field: 'jours' | 'template' | 'statutFin', value: string) => {
    setFormData((prev) => ({
      ...prev,
      relances: prev.relances.map((relance) =>
        relance.id === id ? { ...relance, [field]: value } : relance
      ),
    }));
  };

  const handleRelanceTemplateToggle = (id: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      relances: prev.relances.map((relance) =>
        relance.id === id
          ? { ...relance, useDifferentTemplate: checked, template: checked ? relance.template : '' }
          : relance
      ),
    }));
  };

  const addRelance = () => {
    setFormData((prev) => ({
      ...prev,
      relances: [
        ...prev.relances,
        {
          id: `relance-${Date.now()}`,
          jours: '',
          useDifferentTemplate: false,
          template: '',
          statutFin: '',
        },
      ],
    }));
  };

  const removeRelance = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      relances: prev.relances.filter((relance) => relance.id !== id),
    }));
  };

  const closeStatusModal = () => {
    setStatusActionTarget(null);
    setPendingStatus(null);
  };

  const handleConfirmStatusAction = () => {
    if (!statusActionTarget || !pendingStatus) {
      return;
    }

    setAllNotifications((prev) =>
      prev.map((notification) =>
        notification.id === statusActionTarget.id ? { ...notification, status: pendingStatus } : notification
      )
    );
    closeStatusModal();
  };

  const handleEnregistrer = () => {
    const statutDeclLabel = findSelectLabelFromValue(STATUT_DECLENCHEUR_OPTIONS, formData.statutCible);
    const solutionLabel = findSelectLabelFromValue(SOLUTION_OPTIONS, formData.solution);
    const marqueLabel = findSelectLabelFromValue(MARQUE_OPTIONS, formData.marqueProduit);
    const categorieLabel = findSelectLabelFromValue(CATEGORIE_OPTIONS, formData.categorieProduit);
    const acteurLabel = findSelectLabelFromValue(ACTEUR_OPTIONS, formData.acteur);
    const garantieLabel = findSelectLabelFromValue(GARANTIE_OPTIONS, formData.garantie);
    const cibleLabel = findSelectLabelFromValue(DESTINATAIRE_OPTIONS, formData.cible);
    const typeClientLabel =
      formData.typeClient === 'pro' ? 'Pro' : formData.typeClient === 'individuel' ? 'Individuel' : '';

    const contexteParts: string[] = [];
    if (statutDeclLabel) {
      contexteParts.push(statutDeclLabel);
    }
    if (solutionLabel) {
      contexteParts.push(solutionLabel);
    }
    const contexte = contexteParts.length > 0 ? contexteParts.join(' · ') : '—';

    const contexteDetails: ContexteDetails = {};
    if (statutDeclLabel) {
      contexteDetails.statutCible = statutDeclLabel;
    }
    if (solutionLabel) {
      contexteDetails.solution = solutionLabel;
    }
    if (typeClientLabel) {
      contexteDetails.typeClient = typeClientLabel;
    }
    if (marqueLabel) {
      contexteDetails.marqueProduit = marqueLabel;
    }
    if (categorieLabel) {
      contexteDetails.categorieProduit = categorieLabel;
    }
    if (acteurLabel) {
      contexteDetails.acteur = acteurLabel;
    }
    if (garantieLabel) {
      contexteDetails.garantie = garantieLabel;
    }

    const firstRelanceStatutFin = formData.relances.find((r) => r.statutFin.trim() !== '');
    if (firstRelanceStatutFin) {
      contexteDetails.statutFin = firstRelanceStatutFin.statutFin.trim();
    }

    const templateRaw = formData.templateEmail.trim();
    const template = templateRaw
      ? `${templateRaw.replace(/\s+/g, '_')}_FR`
      : `campagne_${Date.now()}_FR`;

    const firstRelanceWithJours = formData.relances.find((r) => r.jours.trim() !== '');
    let relance: number | undefined;
    if (firstRelanceWithJours) {
      const parsed = Number.parseInt(firstRelanceWithJours.jours, 10);
      if (!Number.isNaN(parsed)) {
        relance = parsed;
      }
    }

    const relanceAvecTemplate = formData.relances.find((r) => r.useDifferentTemplate && r.template.trim() !== '');
    const templateRelance = relanceAvecTemplate
      ? `${relanceAvecTemplate.template.trim().replace(/\s+/g, '_')}_FR`
      : undefined;

    const campaignName = formData.nomCampagne.trim() || 'Nouvelle campagne';
    const today = new Date().toISOString().slice(0, 10);
    const existing = editingNotificationId
      ? allNotifications.find((n) => n.id === editingNotificationId)
      : undefined;

    const newNotification: Notification = {
      id: editingNotificationId ?? `camp-${Date.now()}`,
      name: campaignName,
      type: formData.type,
      status: existing?.status ?? 'draft',
      contexte,
      contexteDetails: Object.keys(contexteDetails).length > 0 ? contexteDetails : undefined,
      cible: cibleLabel || '—',
      template,
      relance,
      templateRelance,
      createdAt: existing?.createdAt ?? today,
      updatedAt: today,
    };

    if (editingNotificationId) {
      setAllNotifications((prev) =>
        prev.map((n) => (n.id === editingNotificationId ? newNotification : n))
      );
    } else {
      setAllNotifications((prev) => [newNotification, ...prev]);
    }

    setFormData(getInitialFormData());
    setEditingNotificationId(null);
    setIsAdditionalSectionOpen(false);
    setIsDrawerOpen(false);
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Heading level={1}>
          Mes notifications
        </Heading>
        <Button
          type={BUTTON_TYPES.BUTTON}
          onClick={() => {
            setEditingNotificationId(null);
            setFormData(getInitialFormData());
            setIsAdditionalSectionOpen(false);
            setIsDrawerOpen(true);
          }}
          primary
        >
          Créer
        </Button>
      </HeaderContainer>

      <Panel>
        <SearchAndFiltersContainer>
          <SearchInputWrapper>
            <SearchInput
              type="text"
              placeholder="Rechercher par nom de campagne"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInputWrapper>

          <FiltersContainer>
          {(['cible', 'publication', 'status', 'relance', 'statutDeclencheur'] as const).map((filterType) => {
            const isActive = selectedFilters[filterType].length > 0;
            const isMenuOpen = openFilterMenu === filterType;
            const selectedCount = selectedFilters[filterType].length;

            return (
              <div key={filterType} ref={filterType === openFilterMenu ? filterMenuRef : null} style={{ position: 'relative' }}>
                <FilterButtonStyled
                  $isActive={isActive || isMenuOpen}
                  onClick={(e) => handleFilterButtonClick(filterType, e)}
                >
                  <FilterIconWrapper>
                    {getFilterIcon(filterType)}
                  </FilterIconWrapper>
                  <span>{getFilterLabel(filterType)}</span>
                  {selectedCount > 0 && <FilterNumber>{selectedCount}</FilterNumber>}
                  <FilterChevronWrapper $isMenuOpen={isMenuOpen}>
                    <ChevronDownIcon size="16px" />
                  </FilterChevronWrapper>
                </FilterButtonStyled>

                {isMenuOpen && (
                  <FilterMenuDropdown
                    $isOpen={isMenuOpen}
                    $top={filterMenuPosition.top}
                    $left={filterMenuPosition.left}
                    onClick={(e) => e.stopPropagation()}
                  >
                  {filterOptions[filterType].map((option) => {
                    if (option.value === undefined) return null;
                    const optionValue = option.value;
                    const isSelected = selectedFilters[filterType].includes(String(optionValue));
                    return (
                      <FilterMenuItem key={String(optionValue)}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleFilterToggle(filterType, optionValue)}
                        />
                        <span>
                          {option.label} ({option.count})
                        </span>
                      </FilterMenuItem>
                    );
                  })}
                  </FilterMenuDropdown>
                )}
              </div>
            );
          })}
          </FiltersContainer>
        </SearchAndFiltersContainer>

        <Datatable<Notification>
          hits={filteredNotifications}
          columns={columns as any}
          getRowId={(row) => row.id}
          hasPagination={true}
          pageSize={10}
        />
      </Panel>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingNotificationId(null);
        }}
        position={DrawerPosition.Right}
        width={600}
      >
        {drawerContentReady ? (
        <DrawerContent>
          <DrawerHeader>
            <Heading level={2}>
              {editingNotificationId ? 'Modifier la notification' : 'Nouvelle notification'}
            </Heading>
          </DrawerHeader>

          {/* Section 1: Segment */}
          <FormSection style={{ order: 2 }}>
            <SectionTitle>Segment</SectionTitle>
            <FormFields>
              <div>
                <TypeClientLabel id="statut-declencheur-label">
                  Statut déclencheur
                  <RequiredAsterisk>*</RequiredAsterisk>
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.statutCible || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      statutCible: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="statut-declencheur-label" id="statut-declencheur-select">
                    <SelectPrimitive.Value placeholder="Sélectionner un statut" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {STATUT_DECLENCHEUR_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="solution-label">
                  Solution
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.solution || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      solution: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="solution-label" id="solution-select">
                    <SelectPrimitive.Value placeholder="Sélectionner une solution" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {SOLUTION_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="categorie-label">
                  Catégorie du produit
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.categorieProduit || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      categorieProduit: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="categorie-label" id="categorie-select">
                    <SelectPrimitive.Value placeholder="Sélectionner une catégorie" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {CATEGORIE_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="marque-label">
                  Marque du produit
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.marqueProduit || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      marqueProduit: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="marque-label" id="marque-select">
                    <SelectPrimitive.Value placeholder="Sélectionner une marque" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {MARQUE_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="acteur-label">
                  Acteur du dossier
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.acteur || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      acteur: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="acteur-label" id="acteur-select">
                    <SelectPrimitive.Value placeholder="Sélectionner un acteur" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {ACTEUR_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="type-client-label">
                  Type client
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.typeClient || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      typeClient: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="type-client-label" id="type-client-select">
                    <SelectPrimitive.Value placeholder="Sélectionner un type" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        <TypeClientSelectItem value="pro">
                          <SelectPrimitive.ItemText>Pro</SelectPrimitive.ItemText>
                        </TypeClientSelectItem>
                        <TypeClientSelectItem value="individuel">
                          <SelectPrimitive.ItemText>Individuel</SelectPrimitive.ItemText>
                        </TypeClientSelectItem>
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
              <div>
                <TypeClientLabel id="garantie-label">
                  Garantie
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.garantie || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      garantie: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="garantie-label" id="garantie-select">
                    <SelectPrimitive.Value placeholder="Sélectionner une garantie" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {GARANTIE_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
            </FormFields>
          </FormSection>

          {/* Section 2: Campagne */}
          <FormSection style={{ order: 1 }}>
            <SectionTitle>Campagne</SectionTitle>
            <FormFields>
              <Input
                label={(
                  <span>
                    Nom de la campagne
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </span>
                )}
                value={formData.nomCampagne}
                onChange={handleInputChange('nomCampagne')}
                placeholder="Saisir un nom de campagne"
              />
              <Input
                label={(
                  <span>
                    Template de la communication
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </span>
                )}
                value={formData.templateEmail}
                onChange={handleInputChange('templateEmail')}
                placeholder="Saisir la référence du template"
              />
              <div>
                <TypeClientLabel id="destinataire-label">
                  Destinataire
                  <RequiredAsterisk>*</RequiredAsterisk>
                </TypeClientLabel>
                <SelectPrimitive.Root
                  value={formData.cible || undefined}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      cible: value ?? '',
                    }));
                  }}
                >
                  <TypeClientSelectTrigger aria-labelledby="destinataire-label" id="destinataire-select">
                    <SelectPrimitive.Value placeholder="Sélectionner un destinataire" />
                    <TypeClientSelectIcon>
                      <ChevronDownIcon size="16px" />
                    </TypeClientSelectIcon>
                  </TypeClientSelectTrigger>
                  <SelectPrimitive.Portal>
                    <TypeClientSelectContent position="popper" sideOffset={4}>
                      <SelectPrimitive.Viewport>
                        {DESTINATAIRE_OPTIONS.map((option) => (
                          <TypeClientSelectItem key={option.value} value={option.value}>
                            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                          </TypeClientSelectItem>
                        ))}
                      </SelectPrimitive.Viewport>
                    </TypeClientSelectContent>
                  </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
              </div>
            </FormFields>
          </FormSection>

          {/* Section 3: Paramètres supplémentaires */}
          <FormSection style={{ order: 3 }}>
            <CollapsibleSectionHeader
              type="button"
              onClick={() => setIsAdditionalSectionOpen((prev) => !prev)}
              aria-expanded={isAdditionalSectionOpen}
            >
              <SectionTitle>Paramètres supplémentaires</SectionTitle>
              {isAdditionalSectionOpen ? <ChevronDownIcon size="18px" /> : <ChevronRightIcon size="18px" />}
            </CollapsibleSectionHeader>
            {isAdditionalSectionOpen && (
              <FormFields>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SectionTitle style={{ margin: 0, fontSize: '1rem' }}>Effectuer un rappel</SectionTitle>
                    <AddButton type="button" onClick={addRelance}>
                      + Ajouter
                    </AddButton>
                  </div>
                  {formData.relances.length > 0 && (
                    <HelperText style={{ marginBottom: '0.75rem' }}>
                      Vous pouvez configurer plusieurs rappels avec des paramètres différents. Le template est optionnel - si non spécifié, le template principal sera utilisé.
                    </HelperText>
                  )}
                  {formData.relances.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#999', fontSize: '0.875rem' }}>
                      Aucun rappel configuré. Cliquez sur "Ajouter" pour en créer un.
                    </div>
                  ) : (
                    <RelanceList>
                      {formData.relances.map((relance, index) => (
                        <RelanceItem key={relance.id}>
                          <RelanceFields>
                            <RelanceTopRow>
                              <JoursFieldContainer>
                                <Input
                                  label={`Rappel ${index + 1}`}
                                  value={relance.jours}
                                  onChange={(e) => handleRelanceChange(relance.id, 'jours', e.target.value)}
                                  type="number"
                                  placeholder="Nombre"
                                  suffix="jours"
                                />
                              </JoursFieldContainer>
                              <ToggleFieldContainer>
                                <TemplateToggleContainer>
                                  <span>Utiliser un template différent ?</span>
                                  <TemplateToggleInput
                                    type="checkbox"
                                    checked={relance.useDifferentTemplate}
                                    onChange={(e) => handleRelanceTemplateToggle(relance.id, e.target.checked)}
                                  />
                                </TemplateToggleContainer>
                              </ToggleFieldContainer>
                            </RelanceTopRow>
                            {relance.useDifferentTemplate && (
                              <div style={{ width: '100%' }}>
                                <Input
                                  label="Template du rappel"
                                  value={relance.template}
                                  onChange={(e) => handleRelanceChange(relance.id, 'template', e.target.value)}
                                  placeholder="Saisir un template"
                                />
                              </div>
                            )}
                            <div style={{ width: '100%' }}>
                              <Input
                                label="Statut de fin"
                                value={relance.statutFin}
                                onChange={(e) => handleRelanceChange(relance.id, 'statutFin', e.target.value)}
                                placeholder="Optionnel - Statut à atteindre pour arrêter les rappels"
                              />
                            </div>
                          </RelanceFields>
                          <RemoveButton
                            type="button"
                            onClick={() => removeRelance(relance.id)}
                            aria-label="Supprimer le rappel"
                          >
                            <Trash width={20} height={20} />
                          </RemoveButton>
                        </RelanceItem>
                      ))}
                    </RelanceList>
                  )}
                </div>
              </FormFields>
            )}
          </FormSection>
          <DrawerFooter>
            <Button type={BUTTON_TYPES.BUTTON} onClick={handleEnregistrer} primary>
              Enregistrer
            </Button>
          </DrawerFooter>
        </DrawerContent>
        ) : null}
      </Drawer>
      {statusActionTarget && pendingStatus && (
        <ConfirmOverlay onClick={closeStatusModal}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Confirmation</ConfirmTitle>
            <ConfirmText>
              {pendingStatus === 'inactive'
                ? 'Cette campagne sera désactivée et ne sera plus envoyée. Voulez-vous confirmer ?'
                : 'Cette campagne sera activée et publiée. Voulez-vous confirmer ?'}
            </ConfirmText>
            <ConfirmActions>
              <ConfirmButton type="button" onClick={closeStatusModal}>
                Annuler
              </ConfirmButton>
              <ConfirmButton $primary type="button" onClick={handleConfirmStatusAction}>
                Confirmer
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </PageContainer>
  );
};

export default NotificationPage;
